/**
 * AppICEMAP — Points of Interest (POI) service
 * Fetches default map POIs (restaurants, hospitals, parking, hotels, …) from
 * the OpenStreetMap Overpass API for a given bounding box. No API key needed.
 *
 * Overpass is a shared community resource — we gate by zoom, cap results, cache
 * by rounded bbox, and abort in-flight requests so we stay a good citizen.
 */

const OVERPASS_ENDPOINTS = [
	"https://overpass-api.de/api/interpreter",
	"https://overpass.kumi.systems/api/interpreter",
];

/** Visual + label config per POI type. Icons are Phosphor glyph names. */
export const POI_TYPES = {
	restaurant: { label: "Restaurante", icon: "fork-knife", color: "#e0873f" },
	hospital: { label: "Hospital", icon: "first-aid-kit", color: "#e05f5f" },
	pharmacy: { label: "Farmacia", icon: "pill", color: "#4fb477" },
	parking: { label: "Parqueo", icon: "car-simple", color: "#5b8fd6" },
	fuel: { label: "Gasolinera", icon: "gas-pump", color: "#c9a03f" },
	bank: { label: "Banco", icon: "bank", color: "#8a7fd0" },
	hotel: { label: "Hotel", icon: "bed", color: "#c77fbf" },
	market: { label: "Supermercado", icon: "shopping-cart", color: "#6bb0a0" },
};

/**
 * Map an OSM element's tags to one of our POI types (or null to skip).
 * @param {Record<string,string>} tags
 * @returns {string | null}
 */
function classify(tags) {
	const a = tags.amenity;
	const t = tags.tourism;
	const s = tags.shop;
	if (a === "restaurant" || a === "fast_food" || a === "cafe")
		return "restaurant";
	if (a === "hospital" || a === "clinic" || a === "doctors") return "hospital";
	if (a === "pharmacy") return "pharmacy";
	if (a === "parking") return "parking";
	if (a === "fuel") return "fuel";
	if (a === "bank" || a === "atm") return "bank";
	if (t === "hotel" || t === "guest_house" || t === "hostel") return "hotel";
	if (s === "supermarket") return "market";
	return null;
}

/** Simple in-memory cache keyed by rounded bbox. */
const _cache = new Map();
/** @type {AbortController | null} */
let _abort = null;
/** @type {number} timestamp of the last Overpass request */
let _lastRequestTime = 0;
/** Minimum gap between Overpass requests (respects rate limits) */
const MIN_REQUEST_GAP = 2000;

function bboxKey(s, w, n, e) {
	const r = (x) => Math.round(x * 100) / 100; // ~1km grid
	return `${r(s)},${r(w)},${r(n)},${r(e)}`;
}

/** Wait if we are below the minimum gap since the last request. */
async function _throttle() {
	const now = Date.now();
	const elapsed = now - _lastRequestTime;
	if (elapsed < MIN_REQUEST_GAP) {
		await new Promise((r) => setTimeout(r, MIN_REQUEST_GAP - elapsed));
	}
	_lastRequestTime = Date.now();
}

/**
 * Fetch POIs within the given bounds.
 * @param {{ sw: [number, number], ne: [number, number] }} bounds
 * @returns {Promise<Array<{ id: string, lat: number, lng: number, type: string, name: string }>>}
 */
export async function fetchPOIs({ sw, ne }) {
	const [s, w] = sw;
	const [n, e] = ne;

	const key = bboxKey(s, w, n, e);
	if (_cache.has(key)) return _cache.get(key);

	const query = `[out:json][timeout:20];(
node["amenity"~"^(restaurant|fast_food|cafe|hospital|clinic|doctors|pharmacy|parking|fuel|bank|atm)$"](${s},${w},${n},${e});
node["tourism"~"^(hotel|guest_house|hostel)$"](${s},${w},${n},${e});
node["shop"="supermarket"](${s},${w},${n},${e});
);out body 250;`;

	// Cancel any in-flight request — only the latest viewport matters
	if (_abort) _abort.abort();
	_abort = new AbortController();
	const signal = _abort.signal;

	// Try all endpoints; on 429, wait and retry before giving up
	for (const endpoint of OVERPASS_ENDPOINTS) {
		for (let attempt = 0; attempt < 2; attempt++) {
			try {
				await _throttle();
				const res = await fetch(
					endpoint + "?data=" + encodeURIComponent(query),
					{ method: "GET", headers: { Accept: "application/json" }, signal },
				);
				if (!res.ok) {
					if (res.status === 429) {
						const retryAfter = parseInt(res.headers.get("Retry-After"), 10) || 5;
						await new Promise((r) => setTimeout(r, retryAfter * 1000));
						continue;
					}
					break;
				}

				const data = await res.json();
				const seen = new Set();
				const pois = [];

				for (const el of data.elements || []) {
					if (el.type !== "node" || !el.tags) continue;
					const type = classify(el.tags);
					if (!type) continue;

					// De-dupe by rounded position + type (avoids stacked duplicates)
					const dedupe = `${type}:${el.lat.toFixed(4)}:${el.lon.toFixed(4)}`;
					if (seen.has(dedupe)) continue;
					seen.add(dedupe);

					pois.push({
						id: `poi-${el.id}`,
						lat: el.lat,
						lng: el.lon,
						type,
						name: el.tags.name || POI_TYPES[type].label,
					});
				}

				_cache.set(key, pois);
				// Cap cache size
				if (_cache.size > 40) _cache.delete(_cache.keys().next().value);
				return pois;
			} catch (err) {
				if (err.name === "AbortError") return [];
				// On 429 the loop retries; on other errors break to try next endpoint
				break;
			}
		}
	}

	return [];
}
